<template>
  <admin-input-list-wrapper :is-fetched="isFetched" v-bind="{ headerJustify: 'start' }">
    <template #search>
      <v-row no-gutters>
        <v-col cols="8">
          <data-input
            :data-type="{ name: 'text' }"
            prepend-inner-icon="mdi-magnify"
            :placeholder="$i18n.t('default.find')"
            v-model="search"
            clearable
            height="32"
            hide-details
            :disabled="disabled"
            class="searchInput"
          >
          </data-input>
        </v-col>
      </v-row>
    </template>
    <template v-if="hasAddButton" #button>
      <v-row no-gutters justify="start" class="flex-nowrap mb-2" style="width: 100%">
        <v-col cols="auto">
          <base-button translation-path="button.addSource" @click="showAddNewDatasource()"></base-button>
        </v-col>
        <v-col cols="auto" class="ml-auto">
          <v-menu offset-y>
            <template #activator="{ on }">
              <base-button v-on="on" translation-path="button.importFileWithData" cancel></base-button>
            </template>
            <v-list dense>
              <v-list-item
                v-for="(item, idx) in importDatasourceOptions"
                :key="idx"
                @click="showAddDatasource(item.withGeometry)"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-left">
                    {{ item.title }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
    </template>
    <template #list>
      <v-row no-gutters class="mt-2">
        <v-col>
          <v-data-table
            :headers="headers"
            :items="searchedItems"
            :footer-props="{
              itemsPerPageOptions: [5, 10, 20, 50, 100],
            }"
            :options.sync="options"
            style="border: 1px solid rgba(0, 0, 0, 0.12)"
          >
            <template #[`item.icon`]="{ item }">
              <v-row no-gutters class="flex-nowrap text-body-2" style="display: flex">
                <v-icon style="font-size: 21px" class="mr-2" color="rgba(0,0,0,0.7)">
                  {{ getDataSourceIcon(item.geometry_type) }}
                </v-icon>
                <span
                  v-if="item.srid && item.geometry_type"
                  class="font-italic font-weight-light"
                  style="font-size: 12px; color: rgba(0,0,0,0.59)"
                >
                  {{ `EPSG:${item.srid}` }}
                </span>
              </v-row>
            </template>
            <template #[`item.verbose_name`]="{ item }">
              <v-row no-gutters class="justify-center" style="gap: 0.3rem; flex-wrap: nowrap">
                <v-col cols="auto">
                  {{ item.verbose_name }}
                </v-col>
                <v-col v-if="item.description" cols="auto" class="d-flex items-center">
                  <v-tooltip bottom max-width="500" content-class="tooltip_multiline" color="font">
                    <template #activator="{ on }">
                      <v-icon v-on="on" small color="primary">
                        mdi-help-circle
                      </v-icon>
                    </template>
                    <span>{{ item.description }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>
            <template #[`item.layers`]="{ item }">
              <span style="display: flex; flex-direction: column; gap: 0.3rem">
                <v-row no-gutters v-for="(layer, index) in [...item.layers].splice(0, 3)" :key="index">
                  <v-col class="caption" style="text-align: center; line-height: 0.9rem">
                    {{ layer.name }}
                  </v-col>
                </v-row>
              </span>
              <v-row no-gutters v-if="[...item.layers].length > 3">
                <v-col>
                  <v-tooltip bottom color="font">
                    <template #activator="{ on }">
                      <v-chip x-small v-on="on">...</v-chip>
                    </template>
                    <div v-for="(remainingLayer, index) in item.layers.slice(3)" :key="index">
                      {{ remainingLayer.name }}
                    </div>
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>
            <template #[`item.actions`]="{ item }">
              <dots-menu
                font-size="19px"
                :items="item.dotsItems"
                @delete="showDeleteDatasource(item.name, [...item.layers])"
                @editAttributes="showEditAttributes(item)"
                @manage="showManageDialog(item)"
                @changeSrid="showChangeSridDialog(item)"
                @exportMetadata="handleExportMetadata(item)"
                @copy="copyDatasource(item)"
                @editDescs="editDescs(item)"
                @refreshView="handleRefreshView(item.name)"
                @advancedExpressions="showAdvancedExpressions(item)"
                :disabled="disabled"
              ></dots-menu>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </template>
    <template #dialogs>
      <main-input-dialog
        v-if="isAddingDatasourceLoaded"
        :is-visible.sync="isAddingDatasourceVisible"
        :title-translation="$i18n.t('dialog.addDataSource')"
        agree-text="dialog.agreeAdd"
        :loading="isAddDialogLoading"
        @agree="handleAddDatasource"
        @modalClosed="handleCloseModal"
        :inputs="addDataSourceInputs"
        dialog-width="45%"
        disable-default-form-bottom-padding
        ref="adding"
      >
        <template v-for="(input, index) in addDataSourceInputs" #[`${input.name}.input`]>
          <v-row v-if="input.name === 'importMode'" :key="index">
            <v-col cols="auto">
              <v-radio-group class="mt-0 pt-0 pb-7 input--type" mandatory v-model="fileImportMode">
                <v-row no-gutters style="gap: 1rem">
                  <v-col cols="5">
                    <v-radio value="datasource">
                      <template #label>
                        <v-row no-gutters>
                          <v-col>
                            <div>{{ $i18n.t('dialog.importOnlyDatasource') }}</div>
                            <italic-subheader translation-path="dialog.importOnlyDatasourceSubtitle" />
                          </v-col>
                        </v-row>
                      </template>
                    </v-radio>
                  </v-col>
                  <v-col cols="5">
                    <v-radio value="structure">
                      <template #label>
                        <v-row no-gutters>
                          <v-col>
                            <div>{{ $i18n.t('dialog.importWholeStructure') }}</div>
                            <italic-subheader translation-path="dialog.importWholeStructureSubtitle" />
                          </v-col>
                        </v-row>
                      </template>
                    </v-radio>
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-col v-else-if="input.name === 'file'" :key="index" cols="6">
            <file-input
              v-if="isFileInputLoaded"
              ref="file"
              :options="dropzoneOptions"
              :show-added-files="true"
              :expanded-files-extensions="currentMode?.files.extensions"
              :button-subheaders="fileInputSubheadersTranslations"
              :events="{
                'vdropzone-sending-multiple': sendingEvent,
                'vdropzone-success-multiple': onUploadSuccess,
                'vdropzone-files-added': onFileAdded,
                'vdropzone-error-multiple': onUploadError,
              }"
            ></file-input>
          </v-col>
          <v-col v-else-if="input.name === 'headersTable'" :key="index">
            <v-data-table
              v-if="isFileLoaded && csvHeaders"
              :headers="csvHeaders"
              :items="csvItems"
              class="mt-4"
              style="border: 1px solid rgba(0, 0, 0, 0.12)"
              hide-default-footer
              disable-pagination
            >
              <template #item="data">
                <td v-for="(item, value, itemIndex) in data.item" :key="itemIndex">
                  <data-input v-model="csvHeadersTypes[itemIndex]" class="px-4" v-bind="csvHeaderTypeInput" />
                </td>
              </template>
            </v-data-table>
            <div v-else></div>
          </v-col>
          <v-col v-else cols="6" :key="index">
            <data-input
              v-model="inputsValues[input.name]"
              v-bind="input"
              @change="addingDSParamChange(input.name, $event)"
            />
          </v-col>
        </template>
      </main-input-dialog>
      <main-dialog
        v-if="isDeleteDatasourceLoaded"
        :is-visible.sync="isDeleteDatasourceVisible"
        :title="$i18n.t('dialog.deleteDatasource')"
        :body="
          $i18n.t('dialog.confirmDatasourceDelete', {
            datasource: nameToDelete?.toUpperCase(),
            ...(layersToDelete && { layers: ` (${layersToDelete?.toUpperCase()})` }),
          })
        "
        agree-text="dialog.agreeDelete"
        @agree="handleDeleteDatasource()"
      ></main-dialog>
      <main-input-dialog
        v-if="isManageLoaded"
        :is-visible.sync="isManageVisible"
        :title-translation="$i18n.t('dialog.title.manageDatasource')"
        agree-text="dialog.agreeSave"
        :is-input-dialog="true"
        @agree="handleManageDatasource"
        :inputs="manageDataSourceInputs"
        dialog-width="38%"
      >
        <template v-for="input in manageDataSourceInputs" #[`${input.name}.input`]>
          <v-col :key="input.name" :cols="6">
            <v-row no-gutters>
              <v-col cols="12">
                <template v-if="input.name === 'related_datasources'">
                  <data-input v-model="editingDataSource.related_datasources" clearable v-bind="input">
                    <template #selection="{ item, index }">
                      <v-chip v-if="index < 1" small>
                        <span class="d-inline-block text-truncate">{{ item.verbose_name }}</span>
                      </v-chip>
                      <span v-if="index === 1" class="text-caption grey--text">
                        {{ `(+${$i18n.tc('admin.other', editingDataSource[input.name].length - 1)})` }}
                      </span>
                    </template>
                  </data-input>
                </template>
                <template v-else>
                  <data-input v-model="editingDataSource[input.name]" clearable v-bind="input" />
                </template>
              </v-col>
            </v-row>
          </v-col>
        </template>
      </main-input-dialog>
      <new-data-source-dialog
        v-if="isAddingNewDatasourceLoaded"
        :is-copy="isDatasourceCopy"
        :with-geometry="isAddingDatasourceWithGeometry"
        :datasource-metadata="editingDataSource"
        :is-visible.sync="isAddingNewDatasourceVisible"
      />
      <data-source-attributes-editing
        v-if="isEditAttributesLoaded"
        :is-visible.sync="isEditAttributesVisible"
        :editing-data-source.sync="editingDataSource"
      />
      <change-datasource-srid-dialog
        v-if="isChangeSridDialogLoaded"
        :is-visible.sync="isChangeSridDialogVisible"
        :datasource-metadata="editingDataSource"
      />
      <data-source-descs-editing
        v-if="isEditDescsVisible"
        :is-visible.sync="isEditDescsVisible"
        :editing-data-source.sync="editingDataSource"
      />
      <data-source-advanced-expressions-dialog
        v-if="advancedExpressionsLoaded"
        :is-visible.sync="advancedExpressionsVisible"
        :editing-data-source.sync="editingDataSource"
      />
    </template>
  </admin-input-list-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';

import AdminInputListWrapper from '@/components/AdminInputListWrapper';
import DotsMenu from '@/components/DotsMenu';
import FileInput from '@/components/FileInput';
import DataInput from '@/components/DataInput';
import BaseButton from '@/components/BaseButton';
import ItalicSubheader from '@/components/ItalicSubheader';

export default {
  name: 'DataSourcesSettingsList',
  mixins: [stylePreviewUtilities],
  props: {
    dataSources: {
      type: Array,
      required: true,
    },
    isFetched: {
      type: Boolean,
      default: false,
    },
    hasAddButton: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DataInput,
    BaseButton,
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    FileInput,
    ItalicSubheader,
    NewDataSourceDialog: () => import('@/components/NewDataSourceDialog'),
    ChangeDatasourceSridDialog: () => import('@/components/ChangeDatasourceSridDialog'),
    DataSourceAttributesEditing: () => import('@/components/DataSourceAttributesEditing'),
    DataSourceDescsEditing: () => import('@/components/DataSourceDescsEditing'),
    AdminInputListWrapper,
    DataSourceAdvancedExpressionsDialog: () => import('@/components/DataSourceAdvancedExpressionsDialog'),
  },
  data: () => ({
    options: {
      itemsPerPage: 100,
      page: 1,
    },
    search: '',
    isFileInputLoaded: true,
    layersToDelete: '',
    fileImportMode: 'datasource',
    isAddDialogLoading: false,
    editingDataSource: {},
    isAddingDatasourceVisible: false,
    isAddingDatasourceLoaded: false,
    isAddingNewDatasourceVisible: false,
    isAddingNewDatasourceLoaded: false,
    isAddingDatasourceWithGeometry: false,
    isChangeSridDialogLoaded: false,
    isChangeSridDialogVisible: false,
    isManageVisible: false,
    isManageLoaded: false,
    isEditAttributesVisible: false,
    isEditAttributesLoaded: false,
    isDeleteDatasourceVisible: false,
    isDeleteDatasourceLoaded: false,
    nameToDelete: undefined,
    isDatasourceCopy: false,
    filesType: '',
    csvHeadersTypes: {},
    csvHeaders: null,
    inputsValues: {},
    isFileLoaded: false,
    isEditDescsVisible: false,
    advancedExpressionsLoaded: false,
    advancedExpressionsVisible: false,
  }),
  computed: {
    datasourceNameMaxLength: get('admin/settingsValues@datasource_name_max_length'),
    coordinatesEpsgs: get('map/coordinatesEpsgs'),
    selectedDataSources: get('datasourceSettings/selectedDataSources'),
    isDxfUploadEnabled: get('admin/additionalModules@DXF_UPLOADS.enabled'),
    isGmlUploadEnabled: get('admin/additionalModules@GML_UPLOADS.enabled'),
    isSqlModuleSuperAdminEnabled: get('admin/additionalModules@DATASOURCES_SQL.enabled'),
    isSqlModuleAdminEnabled: get('admin/settingsValues@datasources_sql_module_enabled'),
    dicts: get('admin/dictionaries'),
    layersMetadata: get('layers/metadata'),
    layersMetadataArray() {
      return this.$_objectToArray(this.layersMetadata);
    },
    layers: get('layers/lay,ers'),
    dataTypes: get('layers/dataTypes'),
    excludedDataTypes() {
      return this.dataTypes.filter(d => d.name !== 'list' && d.name !== 'geometric');
    },
    csvHeaderTypeInput() {
      return {
        dataType: {
          name: 'select',
        },
        items: this.excludedDataTypes,
        itemText: 'verbose_name',
        itemValue: 'name',
        hideDetails: true,
      };
    },
    csvItems() {
      const result = this.csvHeaders?.reduce((acc, item) => {
        acc[item.value] = item.value;
        return acc;
      }, {});
      return result ? [result] : [];
    },
    headers() {
      return ['icon', 'verbose_name', 'added_at', 'create_user', 'modify_date', 'modify_user', 'layers', 'actions'].map(
        header => {
          return {
            value: header,
            align: 'center',
            sortable: ['icon', 'actions', 'layers'].includes(header) ? false : true,
            ...(!['icon', 'actions'].includes(header) && { text: this.$i18n.t(`admin.dataSourcesList.${header}`) }),
            ...(['added_at', 'modify_date'].includes(header) && {
              sort: (a, b) => {
                if (!a) return 1;
                if (!b) return -1;
                return this.$dayjs(b, 'L, LTS') - this.$dayjs(a, 'L, LTS');
              },
            }),
          };
        }
      );
    },
    importDatasourceOptions() {
      return [
        {
          title: this.$i18n.t('admin.fileWithGeometry'),
          withGeometry: true,
        },
        {
          title: this.$i18n.t('admin.fileWithoutGeometry'),
          withGeometry: false,
        },
      ];
    },
    computedDataSources() {
      return this.dataSources.map(dataSource => {
        const { name, datasource_type, added_at, owner, layers, last_edit, geometry_type, srid } = dataSource;
        const isSelected = this.selectedDataSources.findIndex(dS => dS.datasource === name) > -1;
        const isSqlDataSource = datasource_type === 'sql_simple_local';
        return {
          ...dataSource,
          added_at: added_at ? this.$dayjs(added_at).format('L, LTS') : null,
          create_user: owner?.user_name,
          modify_date: last_edit?.timestamp ? this.$dayjs(last_edit?.timestamp).format('L, LTS') : null,
          modify_user: last_edit?.user_name,
          layers: layers || [],
          dotsItems: [
            { name: 'editAttributes' },
            { name: 'manage' },
            ...(!isSqlDataSource && geometry_type && srid ? [{ name: 'changeSrid' }] : []),
            { name: 'copy' },
            { name: 'editDescs' },
            { name: 'advancedExpressions', customName: this.$i18n.t('admin.advancedExpressions') },
            { name: 'exportMetadata' },
            ...(isSqlDataSource && this.isSqlModuleSuperAdminEnabled && this.isSqlModuleAdminEnabled
              ? [{ name: 'refreshView' }]
              : []),
            ...(dataSource.predefined || isSelected ? [] : [{ name: 'delete' }]),
          ],
        };
      });
    },
    computedEpsgItems() {
      return this.$_objectToArray(this.coordinatesEpsgs);
    },
    isFileImportDatasource() {
      return this.fileImportMode === 'datasource';
    },
    currentImportMode() {
      /**
       * Currently there're 4 states of importing:
       * datasource_geometry, datasource_withoutgeometry, structure_geometry, structure_nogeometry
       */
      return this.isAddingDatasourceWithGeometry
        ? `${this.fileImportMode}_geometry`
        : `${this.fileImportMode}_withoutgeometry`;
    },
    importModesMetadata() {
      const datasourceNameInput = {
        name: 'dataSourceName',
        value: '',
        translationPath: 'dialog.dataSourceName',
        cols: '12',
        rules: [v => !!v || v == '0' || 'rules.required'],
        dataType: {
          name: 'text',
          max_length: this.datasourceNameMaxLength,
        },
      };
      const datasourceDescriptionInput = {
        name: 'dataSourceDescription',
        value: '',
        translationPath: 'dialog.datasourceDescription',
        subtitle: this.$i18n.t('default.optional'),
        cols: '12',
        rows: 3,
        dataType: {
          name: 'textarea',
        },
      };
      const fileInput = {
        name: 'file',
        translationPath: 'dialog.file',
        align: 'start',
        isCustom: true,
        ...(this.fileImportMode === 'datasource' ? { inputClasses: ['pt-3'] } : {}),
      };
      const importStructureMetadata = {
        files: {
          translations: {
            subtitlePath: 'dialog.dataSourceCoding',
            extensions: 'JSON',
          },
          extensions: [
            {
              name: 'json',
              exts: ['json'],
              canExtDuplicate: false,
              minFiles: 1,
              maxFiles: 1,
            },
          ],
        },
        inputs: [{ ...fileInput }],
      };
      return {
        datasource_geometry: {
          files: {
            translations: {
              subtitlePath: 'dialog.dataSourceCoding',
              extensions: `Shapefile(dbf, shp, shx)/GeoJSON${this.isDxfUploadEnabled ? '/DXF' : ''}${
                this.isGmlUploadEnabled ? '/GML' : ''
              }`,
            },
            extensions: [
              {
                name: 'shapefile',
                url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/shp`,
                exts: ['shp', 'shx', 'dbf'],
                canExtDuplicate: false,
                minFiles: 3,
                maxFiles: 3,
              },
              {
                name: 'geojson',
                url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/geojson`,
                exts: ['geojson'],
                canExtDuplicate: false,
                minFiles: 1,
                maxFiles: 1,
              },
              ...(this.isDxfUploadEnabled
                ? [
                    {
                      name: 'dxf',
                      url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/dxf`,
                      exts: ['dxf'],
                      canExtDuplicate: false,
                      minFiles: 1,
                      maxFiles: 1,
                    },
                  ]
                : []),
              ...(this.isGmlUploadEnabled
                ? [
                    {
                      name: 'gml',
                      url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/gml`,
                      exts: ['gml'],
                      canExtDuplicate: false,
                      minFiles: 1,
                      maxFiles: 1,
                    },
                  ]
                : []),
            ],
          },
          inputs: [
            ...[datasourceNameInput, datasourceDescriptionInput],
            {
              name: 'srid',
              value: undefined,
              translationPath: 'dialog.body.fileCoordinateSystem',
              rules: [v => (this.computedEpsgItems.find(item => item.srid == v) ? true : false || 'rules.required')],
              dataType: {
                name: 'autocomplete',
              },
              fixedHeight: false,
              items: this.computedEpsgItems,
              itemValue: 'srid',
            },
            {
              name: 'target_srid',
              value: undefined,
              translationPath: 'dialog.body.datasourceCoordinateSystem',
              subtitle: this.$i18n.t('dialog.body.datasourceCoordinateSystemSubtitle'),
              rules: [v => (this.computedEpsgItems.find(item => item.srid == v) ? true : false || 'rules.required')],
              dataType: { name: 'autocomplete' },
              fixedHeight: false,
              items: this.computedEpsgItems,
              itemValue: 'srid',
            },
            ...[fileInput],
          ],
        },
        datasource_withoutgeometry: {
          files: {
            translations: {
              subtitlePath: 'dialog.headersInFirstRow',
              extensions: 'CSV',
            },
            extensions: [
              {
                name: 'csv',
                exts: ['csv'],
                url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/csv`,
                canExtDuplicate: false,
                minFiles: 1,
                maxFiles: 1,
              },
            ],
          },
          inputs: [
            ...[datasourceNameInput, datasourceDescriptionInput],
            {
              name: 'separator',
              value: undefined,
              translationPath: 'dialog.body.separator',
              rules: [v => (this.separatorTypes.find(item => item.value == v) ? true : false || 'rules.required')],
              dataType: {
                name: 'select',
              },
              cols: '12',
              fixedHeight: false,
              items: this.separatorTypes,
            },
            {
              name: 'encoding',
              value: undefined,
              translationPath: 'dialog.body.encoding',
              rules: [v => (this.encodingTypes.find(item => item.value == v) ? true : false || 'rules.required')],
              dataType: {
                name: 'select',
              },
              cols: '12',
              fixedHeight: false,
              items: this.encodingTypes,
            },
            ...[fileInput],
            {
              name: 'headersTable',
              cols: '12',
              leftCol: 0,
            },
          ],
        },
        structure_geometry: importStructureMetadata,
        structure_withoutgeometry: importStructureMetadata,
      };
    },
    currentMode() {
      return this.importModesMetadata[this.currentImportMode];
    },
    encodingTypes() {
      return [
        {
          value: 'utf-8',
          text: 'UTF-8',
        },
        {
          value: 'windows-1250',
          text: 'Windows-1250',
        },
        {
          value: 'iso-8859-2',
          text: 'ISO 8859-2',
        },
      ];
    },
    separatorTypes() {
      return [
        {
          value: ',',
          text: this.$i18n.t('chars.comma'),
        },
        {
          value: ';',
          text: this.$i18n.t('chars.semicolon'),
        },
        {
          value: '\t',
          text: this.$i18n.t('chars.tabulator'),
        },
      ];
    },
    addDataSourceInputs() {
      return [
        {
          name: 'importMode',
          cols: '12',
          leftCol: 0,
        },
        ...(this.currentMode?.inputs || []),
      ];
    },
    manageDataSourceInputs() {
      return [
        {
          name: 'verbose_name',
          translationPath: 'dialog.body.dataSourceName',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'text',
            max_length: this.datasourceNameMaxLength,
          },
        },
        {
          name: 'description',
          translationPath: 'dialog.datasourceDescription',
          subtitle: this.$i18n.t('default.optional'),
          cols: '12',
          rows: 3,
          dataType: {
            name: 'textarea',
          },
        },
        {
          name: 'related_datasources',
          translationPath: 'dialog.dataSourceRelatedDatasources',
          subtitle: this.$i18n.t('default.optional'),
          cols: '12',
          dataType: { name: 'select' },
          multiple: true,
          items: this.layersMetadataArray.filter(lM => lM.datasource_name !== this.editingDataSource.name),
          itemText: 'verbose_name',
          itemValue: 'name',
        },
      ];
    },
    dropzoneOptions() {
      const self = this;
      return {
        url: `${process.env.VUE_APP_API_URL}/dataio/data_sources/upload/shp`,
        method: 'post',
        autoProcessQueue: false,
        acceptedFiles: self.getAcceptedFiles(),
        uploadMultiple: true,
        maxFiles: 3,
        parallelUploads: 3,
        paramName(i) {
          const files = self.$refs.file[0]?.$refs.dropzone.getAcceptedFiles();
          const file = files[i];
          return file.name.split('.').pop();
        },
      };
    },
    fileInputSubheadersTranslations() {
      return [
        {
          type: 'straight',
          value: this.$i18n.t('dialog.dataSourceRequiredFiles', {
            value: this.currentMode?.files.translations.extensions,
          }),
        },
        {
          type: 'path',
          value: this.currentMode?.files.translations.subtitlePath,
        },
      ];
    },
    searchedItems() {
      if (!this.search) {
        return this.sortItems();
      }
      return this.sortItems().filter(a =>
        [a.verbose_name, a.create_user, a.modify_user, a.added_at, a.modify_date].some(
          x => x && x.toLowerCase().includes(this.search.toLowerCase())
        )
      );
    },
  },
  watch: {
    'inputsValues.separator'(nV, oV) {
      this.onFileMetadataChanged(!(nV === ';' && !oV));
    },
    'inputsValues.encoding'() {
      this.onFileMetadataChanged(false);
    },
    fileImportMode(nV, oV) {
      if (!oV) return;
      const srid = localStorage.getItem('import_ds_srid');
      this.inputsValues = { ...(nV === 'datasource' && srid ? { target_srid: +srid } : {}) };
      this.$refs.file[0]?.$refs.dropzone.removeAllFiles();
      this.$refs.file[0]?.$refs.dropzone.setOption('acceptedFiles', this.getAcceptedFiles());
      // Rerendering dropzone to refresh accepted files within OS file explorer
      this.isFileInputLoaded = false;
      this.$nextTick(() => {
        this.isFileInputLoaded = true;
      });
    },
  },
  methods: {
    deleteDataSource: call('layers/deleteDataSource'),
    editDataSource: call('layers/editDataSourceMetadata'),
    getDataSources: call('layers/getLayersMetadata'),
    getDatabaseSrid: call('map/getDatabaseSrid'),
    getDicts: call('admin/getDictionaries'),
    postDict: call('admin/postDictionary'),
    addDataSource: call('layers/addDataSource'),
    addLayerAction: call('layers/addLayer'),
    editLayerAction: call('layers/editLayer'),
    deleteDict: call('admin/deleteDictionary'),
    getDataTypes: call('layers/getDataTypes'),
    refreshSqlView: call('sql/refreshSqlView'),
    onFileMetadataChanged(clearTypes) {
      if (this.$refs.file[0]?.$refs?.dropzone?.getAcceptedFiles().length > 0) {
        this.generateCsvHeadersTableData(this.$refs.file[0]?.$refs?.dropzone?.getAcceptedFiles()[0], clearTypes);
      }
    },
    getDatasourceExportMetadata(datasourceMetadata) {
      let dataSourceData = {
        datasource_verbose_name: datasourceMetadata.verbose_name,
      };
      let dictsArray = [];
      dataSourceData.attributes = [];
      const attributes = JSON.parse(JSON.stringify(datasourceMetadata)).attributes_schema.attributes;
      dataSourceData.srid = datasourceMetadata.srid || this.$_config.defaultEpsg.split(':')[1];
      dataSourceData.geometry_type = datasourceMetadata.geometry_type;
      attributes
        .filter(
          attr =>
            !attr.predefined && !attr.read_only && !['id'].includes(attr.name) && attr.data_type.name !== 'geometric'
        )
        .forEach(attr => {
          const { verbose_name, name, data_type, measurement_unit, dict_id } = attr;
          if (dict_id) {
            const dictMetadata = this.dicts[dict_id];
            dictsArray.push({
              dictId: dict_id,
              name: `COPYIMPORT_${dictMetadata.name}`,
              data_type: dictMetadata.data_type,
              values: dictMetadata.values,
              colors: dictMetadata.colors,
            });
          }
          delete data_type.scada3_display_attribute;
          dataSourceData.attributes.push({
            verbose_name,
            name,
            data_type,
            ...(dict_id && { dict_id }),
            ...(measurement_unit && { measurement_unit_id: measurement_unit.id }),
          });
        });
      return {
        dictionaries: dictsArray,
        datasource: dataSourceData,
      };
    },
    getDatasourceLayersExportMetadata(layers, datasourceInitialAttrsSchema) {
      const excludedAttributesNames = [datasourceInitialAttrsSchema.id_name];
      return layers.map(layer => {
        const {
          name,
          filter_expression,
          data_source_name,
          style_web,
          image_attributes,
          as_gallery,
          form_schema,
          layer_scope,
        } = this.layers[layer.id];
        const filteredFormSchemaElements = form_schema.elements.reduce((acc, element) => {
          acc.push({
            ...element,
            elements: element.elements.filter(attr => ![...excludedAttributesNames].includes(attr.attribute)),
          });
          return acc;
        }, []);
        return {
          form_schema: { elements: filteredFormSchemaElements },
          data: {
            name: `COPYIMPORT_${name}`,
            layer_scope,
            filter_expression,
            data_source: data_source_name,
            permissions: [],
            image_attributes,
            as_gallery,
            style_web,
          },
        };
      });
    },
    showChangeSridDialog(item) {
      this.editingDataSource = item;
      this.isChangeSridDialogLoaded = this.isChangeSridDialogVisible = true;
    },
    async handleExportMetadata(datasourceMetadata) {
      try {
        await this.getDicts();
        const dtMeta = this.getDatasourceExportMetadata(datasourceMetadata);
        const dtLayersMeta = this.getDatasourceLayersExportMetadata(
          datasourceMetadata.layers || [],
          datasourceMetadata.attributes_schema
        );
        const allData = {
          ...dtMeta,
          layers: dtLayersMeta,
        };
        const blob = new Blob([JSON.stringify(allData)], { type: 'application/json;charset=utf-8;' });
        this.$_saveFile(URL.createObjectURL(blob), `metadata_${dtMeta.datasource.datasource_verbose_name}.json`);
      } catch (error) {
        console.error(error);
      }
    },
    async handleImportMetadata(data) {
      let dictMapping = {};
      try {
        await this.getDicts();
        // Adding dicts
        for await (const dictMetadata of data.dictionaries) {
          try {
            const emptyFile = new File([''], '');
            const pictures = dictMetadata.colors.map(() => emptyFile);
            const formData = new FormData();
            formData.append('name', dictMetadata.name);
            formData.append('data_type', dictMetadata.data_type);
            formData.append('values', JSON.stringify(dictMetadata.values));
            formData.append('colors', JSON.stringify(dictMetadata.colors));
            for (let picture of pictures) formData.append('files', picture);
            const newDictId = await this.postDict(formData);
            dictMapping[dictMetadata.dictId] = newDictId;
          } catch {
            throw Error();
          }
        }
        // Adding datasource
        let r;
        try {
          data.datasource.attributes = data.datasource.attributes.map(attr => {
            return {
              ...attr,
              ...(attr.dict_id && { dict_id: dictMapping[attr.dict_id] }),
            };
          });
          r = await this.addDataSource({ payload: data.datasource, skipMetadataParsing: true });
        } catch {
          throw Error();
        }
        // Adding layers
        const newDatasourceName = r.datasource_metadata.name;
        for await (const layer of data.layers) {
          try {
            const payloads = {
              addPayload: {
                skipMetadataParsing: true,
                layer_type: 'features_layers',
                body: layer.data,
              },
              editPayload: {
                scope: layer.data.layer_scope,
                layer_type: 'features_layers',
                skipMetadataParsing: true,
                body: {
                  form_schema: layer.form_schema,
                },
              },
            };
            payloads.addPayload.body.filter_expression = this.$_replaceSubstringInNestedObject(
              payloads.addPayload.body.filter_expression,
              layer.data.data_source,
              newDatasourceName,
              'value'
            );
            payloads.addPayload.body.data_source = newDatasourceName;
            const newLayerId = await this.addLayerAction(payloads.addPayload);
            await this.editLayerAction({
              ...payloads.editPayload,
              layer_id: newLayerId,
            });
          } catch {
            try {
              await this.deleteDataSource(newDatasourceName);
            } catch {
              //
            }
            throw Error();
          }
        }
        await this.getDataSources({
          with_attributes_schema: true,
          with_layers: true,
          with_last_edit: true,
          with_owner: true,
        });
      } catch {
        for await (const dictId of Object.values(dictMapping)) {
          try {
            this.deleteDict(dictId);
          } catch {
            //
          }
        }
      }
    },
    async handleRefreshView(name) {
      try {
        await this.refreshSqlView(name);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successViewRefresh'),
        });
      } catch (error) {
        console.log('error: ', error);
      }
    },
    showAddDatasource(withGeometry) {
      this.isAddingDatasourceWithGeometry = withGeometry;
      this.isAddingDatasourceLoaded = true;
      this.isAddingDatasourceVisible = true;
      const srid = localStorage.getItem('import_ds_srid');
      if (withGeometry && srid) this.inputsValues.target_srid = +srid;
    },
    showAddNewDatasource() {
      this.isDatasourceCopy = false;
      this.editingDataSource = {};
      this.isAddingNewDatasourceVisible = true;
      this.isAddingNewDatasourceLoaded = true;
    },
    showEditAttributes(datasource) {
      this.editingDataSource = JSON.parse(JSON.stringify(datasource));
      this.isEditAttributesLoaded = true;
      this.isEditAttributesVisible = true;
    },
    copyDatasource(datasource) {
      this.isDatasourceCopy = true;
      this.editingDataSource = JSON.parse(JSON.stringify(datasource));
      this.isAddingNewDatasourceVisible = true;
      this.isAddingNewDatasourceLoaded = true;
    },
    editDescs(datasource) {
      this.editingDataSource = { ...datasource };
      this.isEditDescsVisible = true;
    },
    showManageDialog(datasource) {
      this.editingDataSource = {};
      this.$nextTick(() => {
        // 💩 'related_datasources' select with 'multiple' value has some problem here without clearing 'editingDataSource' before
        this.editingDataSource = { ...datasource };
        this.isManageLoaded = true;
        this.isManageVisible = true;
      });
    },
    showDeleteDatasource(name, layers) {
      this.nameToDelete = name;
      this.layersToDelete = layers.map(layer => layer.name).join(', ');
      this.isDeleteDatasourceLoaded = true;
      this.isDeleteDatasourceVisible = true;
    },
    showAdvancedExpressions(datasource) {
      this.editingDataSource = { ...datasource };
      this.advancedExpressionsLoaded = true;
      this.advancedExpressionsVisible = true;
    },
    getCsvAttributesMetadata() {
      return this.csvHeaders.reduce((acc, item, index) => {
        acc.push({
          data_type: {
            name: this.csvHeadersTypes[index],
          },
          name: item.value,
        });
        return acc;
      }, []);
    },
    async handleAddDatasource() {
      if (this.$refs.file[0]?.$refs.dropzone.getAcceptedFiles().length < 1) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.filesRequired'),
        });
        return;
      }
      this.isAddDialogLoading = true;
      if (this.isFileImportDatasource) {
        this.$refs.file[0]?.$refs.dropzone.processQueue();
      } else {
        this.isAddDialogLoading = true;
        const fileText = await this.$refs.file[0]?.$refs.dropzone.getAcceptedFiles()[0].text();
        const fileContent = JSON.parse(fileText);
        try {
          await this.handleImportMetadata(fileContent);
          this.isAddingDatasourceVisible = false;
        } finally {
          this.$refs.file[0]?.$refs.dropzone.removeAllFiles();
          this.isFileLoaded = null;
          this.isAddDialogLoading = false;
        }
      }
    },
    handleCloseModal() {
      this.$refs.file[0]?.$refs.dropzone.removeAllFiles();
      this.inputsValues = {};
      this.isFileLoaded = null;
    },
    handleDeleteDatasource() {
      this.deleteDataSource(this.nameToDelete);
      this.isDeleteDatasourceVisible = false;
    },
    handleManageDatasource() {
      const payload = {
        name: this.editingDataSource.name,
        body: this.manageDataSourceInputs.reduce(
          (acc, item) => ({ ...acc, [item.name]: this.editingDataSource[item.name] }),
          {}
        ),
      };
      this.editDataSource(payload);
      this.isManageVisible = false;
    },
    sendingEvent(files, xhr, formData) {
      formData.append('datasource_name', this.inputsValues['dataSourceName']);
      if (this.inputsValues['dataSourceDescription'])
        formData.append('description', this.inputsValues['dataSourceDescription']);
      if (this.currentImportMode === 'datasource_withoutgeometry') {
        formData.append('encoding', this.inputsValues['encoding']);
        formData.append('separator', this.inputsValues['separator']);
        formData.append('attributes', JSON.stringify(this.getCsvAttributesMetadata()));
      } else {
        formData.append('srid', this.inputsValues['srid']);
        formData.append('target_srid', this.inputsValues['target_srid']);
      }
    },
    async generateCsvHeadersTableData(file, clearTypes = true) {
      const reader = new FileReader();
      reader.readAsText(file, this.inputsValues['encoding']);
      reader.onloadend = () => {
        const fileData = reader.result;
        const fileHeaders = fileData.split('\n')[0].split(this.inputsValues['separator'] || ';');
        if (clearTypes) this.csvHeadersTypes = {};
        this.csvHeaders = fileHeaders.map((hd, index) => {
          if (clearTypes) this.csvHeadersTypes[index] = 'text';
          return {
            value: hd,
            text: hd,
            align: 'center',
            width: 200,
            sortable: false,
          };
        });
        this.isFileLoaded = true;
      };
    },
    async onFileAdded(file) {
      if (file[0].type !== 'text/csv') return;
      this.csvHeadersTypes = {};
      await this.generateCsvHeadersTableData(file[0]);
    },
    onUploadSuccess() {
      this.$refs.adding.$refs.form.reset();
      this.isAddingDatasourceVisible = false;
      this.$refs.file[0]?.$refs.dropzone.removeAllFiles();
      this.isFileLoaded = null;
      this.isAddDialogLoading = false;
      this.getDataSources({ with_attributes_schema: true, with_layers: true, with_last_edit: true, with_owner: true });
    },
    onUploadError() {
      this.$refs.file[0]?.$refs.dropzone.removeAllFiles();
      this.isFileLoaded = null;
      this.isAddDialogLoading = false;
    },
    sortItems() {
      return this.computedDataSources?.sort((a, b) => a.verbose_name.localeCompare(b.verbose_name));
    },
    getAcceptedFiles() {
      let files = [];
      if (this.isFileImportDatasource) {
        if (this.isAddingDatasourceWithGeometry) {
          files = ['.shp', '.dbf', '.shx', '.geojson'];
          if (this.isDxfUploadEnabled) files.push('.dxf');
          if (this.isGmlUploadEnabled) files.push('.gml');
        } else {
          files = ['.csv'];
        }
      } else {
        files = ['.json'];
      }
      return files.join(',');
    },
    addingDSParamChange(name, value) {
      if (name === 'target_srid' && value) localStorage.setItem('import_ds_srid', value);
    },
  },
  mounted() {
    this.getDatabaseSrid();
    this.getDataTypes();
  },
};
</script>

<style lang="scss" scoped>
.layer-element {
  @include border();
  text-align: left;
}
::v-deep td {
  padding-top: 0.3rem !important;
  padding-bottom: 0.3rem !important;
  height: 65px !important;
  min-height: 65px !important;
}
::v-deep {
  .searchInput {
    .v-input__slot {
      min-height: 0px !important;
      padding: 0 4px !important;
    }
    .v-input__append-inner {
      margin: 6px !important;
    }
    .v-input__prepend-inner {
      .v-input__icon {
        .v-icon {
          font-size: 16px;
          padding-bottom: 5px;
        }
      }
    }
    .v-text-field__slot input {
      font-size: 12px !important;
    }
  }
}
</style>
